<script setup lang="ts">
import RawHtmlRenderer from "~/components/Common/RawHtmlRenderer.vue";
import type { ProductType, CustomFields } from "@/types";

const mainStore = useMainStore();
const cardType = computed(() => mainStore?.globalSettings?.CardType);

type PropsType = {
  product: ProductType;
  ctaButton?: boolean;
  link?: string;
  bestValue?: boolean;
  buyNow?: () => void;
  isChangePlan?: boolean;
  modalImages?: Array<{ url: string; alternativeText?: string; name?: string }>;
};

const props = defineProps<PropsType>();
console.log("buyNow", props.buyNow);
const customFields = computed<CustomFields | undefined>(
  (): CustomFields | undefined => {
    return props.product?.offerServiceTemplate[0]?.serviceTemplate
      ?.customFields;
  }
);

// Check if this is a best value product - MODIFIED to respect prop
const isBestValue = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return false immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show best value tag in Change Plan journey
      return false;
    }
  }

  // CRITICAL FIX: Always respect the bestValue prop passed from parent
  // This ensures consistent behavior across home page and change plan
  if (props.bestValue !== undefined) {
    return props.bestValue;
  }
  // Fallback to data-driven approach only if no prop is provided
  return (
    customFields.value?.pim?.goCommerce?.metadataBestValue === "true" ||
    customFields.value?.pim?.goCommerce?.isFeaturedProduct === true
  );
});

// Determine if we should show the Buy Now button
// We hide it in the Change Plan journey or any dashboard context
const shouldShowBuyNowButton = computed(() => {
  if (typeof window === "undefined") return false;

  const currentUrl = window.location.href || "";
  const isChangePlanJourney =
    currentUrl.includes("/change-plan") ||
    currentUrl.includes("/account/dashboard");

  // Check if the buyNow prop exists and we're not in the Change Plan journey
  return typeof props.buyNow === "function" && !isChangePlanJourney;
});

// Get the raw quota data GB from the product
const baseQuotaDataGb = computed(() => {
  return customFields.value?.pim?.goCommerce?.quotaDataGb || 0;
});


// Check if we're in Change Plan context
const isChangePlanContext = computed(() => {
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    return (
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard?view=my-plans") ||
      currentUrl.includes("/account/dashboard") ||
      currentUrl.includes("planchange")
    );
  }
  return false;
});

// Get the bonus data amount in GB (as a number, not a string)
const bonusDataGbNumber = computed(() => {
  // CRITICAL: If we're in the Change Plan context, NEVER show bonus data
  // as it's only for new customers, not for plan changes
  if (isChangePlanContext.value) {
    console.log(
      "[Plan/Card] DISABLING BONUS DATA for Change Plan context:",
      props.product?.name
    );
    return 0;
  }

  // Get allowedDiscountPlans array
  const allowedDiscountPlans = props.product?.allowedDiscountPlans;
  if (!allowedDiscountPlans || !Array.isArray(allowedDiscountPlans)) {
    return 0;
  }
  // Loop through discount plans
  for (const discountPlan of allowedDiscountPlans) {
    // Check the exact structure from the sample JSON
    const discountPlanItems = Array.isArray(discountPlan.discountPlanItem)
      ? discountPlan.discountPlanItem
      : [discountPlan.discountPlanItem];

    for (const item of discountPlanItems) {
      if (
        item?.customFields?.customField &&
        Array.isArray(item.customFields.customField)
      ) {
        // Find the customField with code=bonusDataAmount
        const bonusDataField = item.customFields.customField.find(
          (field) => field.code === "bonusDataAmount"
        );
        if (bonusDataField && bonusDataField.doubleValue) {
          return Math.round(bonusDataField.doubleValue);
        }
      }
    }
  }

  return 0;
});

// Calculate the total data amount (base + bonus)
const quotaDataGb = computed(() => {
  // For Change Plan context, only show the base data amount (no bonus)
  if (isChangePlanContext.value) {
    return baseQuotaDataGb.value;
  }
  // Otherwise, show base + bonus for normal display
  const total = baseQuotaDataGb.value + bonusDataGbNumber.value;
  return total;
});

const productFeatures = computed(() => {
  return props.product?.strapiData?.features || [];
});

// Get promotion description from strapi data
const promotionDescription = computed(() => {
  // CRITICAL: Check if this is product 86 ($30 45GB) and hide promotion in Change Plan journey
  const productObj = props.product || {};
  const productAsAny = productObj as any;

  // Check multiple ways to identify product 86
  const isProduct86 =
    productObj.code === "86" ||
    productAsAny.serviceTemplateCode === "86" ||
    productObj.offerServiceTemplate?.[0]?.serviceTemplate?.code === "86" ||
    productObj.productOfferingPrice?.recurring?.[0]?.id === 86 ||
    productObj.name?.includes("$30 45GB");

  // If we're in the Change Plan journey and this is product 86, return null to hide the promotion
  if (
    isProduct86 &&
    window.location.pathname.includes("/my-plans/change-plan")
  ) {
    console.log(
      "🔴 HIDING promotion description for product 86 in Change Plan journey"
    );
    return "";
  }

  // Default behavior - Get description from Strapi data only
  return props.product?.strapiData?.promotionDescription || "";
});

// Get gift card from strapi product data or fallback to custom implementation
const giftCard = computed(() => {
  // First check for gift card data in Strapi
  const strapiGiftCard = props.product?.strapiData?.giftCard;
  if (strapiGiftCard) return strapiGiftCard;

  // If no gift card in Strapi, return false
  return false;
});

// Format the bonus data amount for display in the bonus tag
const bonusDataAmount = computed(() => {
  // Reuse the bonusDataGbNumber value for consistency
  return bonusDataGbNumber.value > 0 ? `${bonusDataGbNumber.value}GB` : null;
});

/**
 * IGNITE-119: a promotion is not always a price cut. A bonus-data-only plan
 * leaves the price alone and only lifts the allowance, so gating the promo
 * flag and the short T&Cs on discountAmount hid both on those tiles even
 * though Ops had filled in promotionFlagText and metadataBonusDataDescription.
 *
 * Anything that changes what the customer gets counts as a promotion. The
 * price treatment below still keys off the monetary discount, because with no
 * price change there is no original price to strike through.
 *
 * Both inputs already return false/0 in the Change Plan journey, so that
 * exclusion carries over untouched.
 */
const hasPromotion = computed(
  () =>
    (hasDiscount.value && discountAmount.value > 0) ||
    bonusDataGbNumber.value > 0
);

// We already have discountAmount handling the price discount, so no need for a separate implementation

// Product data processing

const productRecordId = computed(() => {
  // Get the service template code (e.g., "93-FR")
  return props.product?.offerServiceTemplate[0]?.serviceTemplate.code;
});

// Extract just the numeric portion of the productRecordId for matching with recurring price IDs
const productRecordNumericId = computed(() => {
  // If the code has a numeric part (like "93-FR" → 93), extract it
  const code = productRecordId.value;
  if (code) {
    // Extract just the numeric part if it exists
    const numericPart = code.match(/^(\d+)/);
    if (numericPart && numericPart[1]) {
      return Number(numericPart[1]);
    }
  }
  // Fallback to the full code for cases where there's no numeric prefix
  return code;
});

const priceObject = computed(() => {
  // Add null check for recurring array before using find
  const recurring = props.product?.productOfferingPrice?.recurring || [];

  // First try to find the price using the numeric ID extracted from the product code
  // This handles cases like product code "93-FR" matching recurring price ID "93"
  let findPrice = recurring.find(
    (price) => price.id === productRecordNumericId.value
  );

  // If no match with numeric ID, try matching by name (for backward compatibility)
  if (!findPrice && productRecordId.value) {
    findPrice = recurring.find((price) =>
      price.name?.includes(productRecordId.value)
    );
  }

  // If still no match, just use the first recurring price if available
  if (!findPrice && recurring.length > 0) {
    console.log("No exact price match found, using first recurring price");
    findPrice = recurring[0];
  }

  return findPrice;
});

const finalPrice = computed(() => {
  // Use the improved price calculation logic with better fallbacks
  // Log the product name for debugging when in development mode
  if (process.env.NODE_ENV !== "production") {
    console.log(
      `Calculating FINAL PRICE for product: ${props.product?.name} (${props.product?.code})`
    );
  }

  // First check if there's a recurring price available, otherwise we may need to use oneTime
  // This ensures we don't incorrectly use a $0 oneTime price for affiliate products
  const recurring = props.product?.productOfferingPrice?.recurring || [];
  if (recurring.length > 0 && !priceObject.value) {
    console.log(
      "Warning: No matching recurring price found by ID, but recurring prices exist"
    );
  }

  // Get price from priceObject or fallback to checking if any recurring price exists
  let price;
  if (priceObject.value?.price?.taxIncludedAmount !== undefined) {
    // Use the matched recurring price
    price = priceObject.value.price.taxIncludedAmount;
  } else if (recurring.length > 0) {
    // If we couldn't match a specific recurring price but recurring prices exist, use the first one
    price = recurring[0].price?.taxIncludedAmount ?? 0;
    console.log("Using first available recurring price:", price);
  } else {
    // If no recurring prices at all, fall back to oneTime (though this shouldn't happen for subscription products)
    price =
      props.product?.productOfferingPrice?.oneTime?.[0]?.price
        ?.taxIncludedAmount ?? 0;
    console.log("No recurring prices found, using oneTime price:", price);
  }

  console.log("Original API price (before any adjustments):", price);

  // If the price is a string (from API response), convert it to a number
  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  // Ensure price is a valid number or default to 0
  let finalValue = typeof price === "number" && !isNaN(price) ? price : 0;

  // We don't need to add 10% as the API already includes tax
  // Just return the exact price from the API

  return finalValue;
});

// Format the price to only show decimals if not .00
const formattedPrice = computed(() => {
  if (finalPrice.value === 0) return "$0";

  // Check if the price has no decimal part
  if (finalPrice.value % 1 === 0) {
    return "$" + finalPrice.value;
  } else {
    return currency(finalPrice.value);
  }
});

// Helper function to check if a value exists and is greater than 0
const isPositiveNumber = (val) => {
  if (typeof val === "string") {
    return parseFloat(val) > 0;
  }
  return typeof val === "number" && val > 0;
};

// Calculate the discount amount directly from API values
const discountAmount = computed(() => {
  // If we have an explicit discount amount in the API, use it
  if (priceObject.value?.discount) {
    const amount = Number(priceObject.value.price.discountAmount);
    if (amount > 0) {
      console.log("Found explicit discount amount:", amount);
      return amount;
    }
  }

  // Otherwise calculate it from the difference between original and discounted prices
  const originalAmount = priceObject.value?.price?.taxIncludedAmount;
  const discountedAmount =
    priceObject.value?.price?.taxIncludedAmountAfterDiscount;

  if (originalAmount && discountedAmount) {
    const original = Number(originalAmount);
    const discounted = Number(discountedAmount);
    if (original > discounted) {
      const calculatedDiscount = original - discounted;
      console.log(
        "Calculated discount from price difference:",
        calculatedDiscount
      );
      return calculatedDiscount;
    }
  }

  // If there's a discount object but no amount info, default to the discountValue if available
  if (priceObject.value?.discount?.discountValue) {
    console.log(
      "Using discountValue from discount object:",
      priceObject.value.discount.discountValue
    );
    return Number(priceObject.value.discount.discountValue);
  }

  // Finally, check allowedDiscountPlans for price discounts
  const allowedDiscountPlans = props.product?.allowedDiscountPlans;
  if (allowedDiscountPlans && Array.isArray(allowedDiscountPlans)) {
    for (const discountPlan of allowedDiscountPlans) {
      const discountPlanItems = Array.isArray(discountPlan.discountPlanItem)
        ? discountPlan.discountPlanItem
        : [discountPlan.discountPlanItem];

      for (const item of discountPlanItems) {
        if (item?.discountPlanItemType === "FIXED" && item?.discountValue > 0) {
          console.log(
            "Found FIXED discount in allowedDiscountPlans:",
            item.discountValue
          );
          return Number(item.discountValue);
        } else if (
          item?.discountPlanItemType === "PERCENTAGE" &&
          item?.discountValue > 0
        ) {
          // For percentage discounts, calculate based on original price
          const original = Number(originalAmount);
          if (!isNaN(original)) {
            const percentDiscount =
              original * (Number(item.discountValue) / 100);
            console.log(
              "Calculated PERCENTAGE discount in allowedDiscountPlans:",
              percentDiscount
            );
            return percentDiscount;
          }
        }
      }
    }
  }

  return 0;
});

// Check if the product has a valid discount
const hasDiscount = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return false immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show discounts in Change Plan journey
      return false;
    }
  }

  // First check if we have the discount object
  if (priceObject.value?.discount) {
    return true;
  }

  // Then check for a discount amount greater than 0
  if (discountAmount.value > 0) {
    return true;
  }

  // Then check if we have an 'after discount' price that differs from the original
  const originalAmount = priceObject.value?.price?.taxIncludedAmount;
  const discountedAmount =
    priceObject.value?.price?.taxIncludedAmountAfterDiscount;

  if (
    originalAmount &&
    discountedAmount &&
    originalAmount !== discountedAmount
  ) {
    console.log("Found different original and discounted amounts");
    return true;
  }

  return false;
});

const originalPrice = computed(() => {
  // Always show the original price even if no discount
  let price = priceObject.value?.price?.taxIncludedAmount;

  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  if (typeof price === "number" && !isNaN(price)) {
    // Format the price similar to formattedPrice
    if (price % 1 === 0) {
      return "$" + price;
    } else {
      return currency(price);
    }
  }

  return null;
});

/**
 * IGNITE-84: bonus Flybuys points roundel (September campaign, 28D plans).
 *
 * Field driven: the roundel renders only while BonusFlybuysPoints is present
 * with a value, so the campaign ends by the backend dropping the field. Buy
 * journey only - never in Change Plan.
 */
const bonusFlybuysPoints = computed(() => {
  if (isChangePlanContext.value) return 0;

  const points = Number(
    (customFields.value?.pim?.goCommerce as any)?.BonusFlybuysPoints
  );
  return Number.isFinite(points) && points > 0 ? points : 0;
});

const discountedPrice = computed(() => {
  if (!hasDiscount.value) {
    console.log("No discount available, using regular price");
    return formattedPrice.value;
  }

  let price = priceObject.value?.price?.taxIncludedAmountAfterDiscount;
  console.log("Discounted price from API:", price);

  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  if (typeof price === "number" && !isNaN(price)) {
    // No need to add 10% tax, use the exact price from the API
    console.log("Final discounted price from API:", price);

    // Format the price similar to formattedPrice
    if (price % 1 === 0) {
      return "$" + price;
    } else {
      return currency(price);
    }
  }

  return formattedPrice.value; // Fall back to regular price if calculation fails
});

// Feature text gets loaded from the correct template field
// (Promotion description moved to combined implementation above)

// Get the feature text from the correct field
const featureText = computed(() => {
    const backendValue = (props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
    ?.goCommerce as any)?.metadataFeatureText;
  if (backendValue) return backendValue;

  
});

const membershipOffer = computed(() => {
  return (
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
      ?.goCommerce?.membershipOffer || ""
  );
});

// Raw metadata bonus data description (used alone in T&Cs when available)
const metadataBonusDataDesc = computed(() => {
  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
    ?.goCommerce?.metadataBonusDataDescription || '';
});

// Get bonus data description text to display after price
const bonusDataDescription = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return null immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show bonus data description in Change Plan journey
      return null;
    }
  }

  // Get bonus data description from metadata
  const metadataDesc =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
      ?.goCommerce?.metadataBonusDataDescription;

  // Return the description directly if available
  if (metadataDesc) return metadataDesc;

  // Only use a fallback if there's actual bonus data and no description
  return bonusDataGbNumber.value > 0
    ? `Includes ${bonusDataGbNumber.value}GB bonus data`
    : null;
});

// Helper function to display plan type based on code
const getPlanTypeDisplay = (code) => {
  if (!code) return "";

  switch (code) {
    case "MONTH-28DAYS":
      return "28 Days";
    case "MONTH-90DAYS":
      return "90 Days";
    case "MONTH_1":
    case "MONTH":
      return "Month";
    case "ANNUAL":
      return "365 Days";
    default:
      return code;
  }
};

// Get the discount details with improved error handling
const discountDetails = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return null immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show discount details in Change Plan journey
      return null;
    }
  }

  // CRITICAL: Check if this is product 86 ($30 45GB) and hide ALL discount details in Change Plan journey
  const productObj = props.product || {};
  const productAsAny = productObj as any;

  // Check multiple ways to identify product 86
  const isProduct86 =
    productObj.code === "86" ||
    productAsAny.serviceTemplateCode === "86" ||
    productObj.offerServiceTemplate?.[0]?.serviceTemplate?.code === "86" ||
    productObj.productOfferingPrice?.recurring?.[0]?.id === 86 ||
    productObj.name?.includes("$30 45GB");

  // If we're in the Change Plan journey and this is product 86, return null to hide all discount details
  if (
    isProduct86 &&
    window.location.pathname.includes("/my-plans/change-plan")
  ) {
    console.log(
      "🔴 HIDING discount offer details for product 86 in Change Plan journey"
    );
    return null; // Return null to prevent displaying any discount details
  }

  // Default behavior - Get discount details as normal
  let discount = null;
  if (priceObject?.value?.discount) {
    // Add null check for allowedDiscountPlans before using find
    const allowedDiscountPlans = props.product?.allowedDiscountPlans || [];
    discount = allowedDiscountPlans.find(
      (discount) =>
        discount.code === priceObject?.value?.discount?.discountPlanCode
    );

    // Set a default description if not provided
    if (discount && discountAmount.value > 0) {
      if (!discount.description) {
        const duration = discount.defaultDuration || 90;
        const unit = discount.durationUnit === "DAY" ? "days" : "months";
        discount.description = `$${Math.round(discountAmount.value)} off for ${duration > 30 ? Math.round(duration / 30) : duration} ${unit}`;
      }
    }
  }
  return discount;
});

const rppPrice = computed(() => {
  return priceObject?.value?.price?.taxIncludedAmount || 0;
});

// Get the defaultDuration from discount object for adaptive discount text
const discountDefaultDuration = computed(() => {
  return priceObject.value?.discount?.defaultDuration || 0;
});

// Get discount type (PERCENTAGE or FIXED_AMOUNT etc.)
const discountItemType = computed(() => {
  return priceObject.value?.discount?.discountPlanItemType || "";
});

// Get raw discount percentage value (e.g. 50 for 50%)
const discountPercentageValue = computed(() => {
  if (discountItemType.value === "PERCENTAGE") {
    const val = Math.abs(Number(priceObject.value?.discount?.discountValue || 0));
    return val;
  }
  return 0;
});

// Build the promo banner text based on discount type and duration
const promoBannerText = computed(() => {
  // Use promotionFlagText from backend if available
  const promotionFlagText = (customFields.value?.pim?.goCommerce as any)?.promotionFlagText;
  if (promotionFlagText) return promotionFlagText;

  const duration = discountDefaultDuration.value;

  if (discountItemType.value === "PERCENTAGE" && discountPercentageValue.value > 0) {
    if (duration > 1) {
      return `Save ${discountPercentageValue.value}% off on first ${duration} recharges`;
    }
    return `Save ${discountPercentageValue.value}% off on first recharge`;
  }

  // Monetary discount (default)
  const amount = Math.round(discountAmount.value * 100) / 100;
  if (duration > 1) {
    return `Save $${amount} on first ${duration} recharges`;
  }
  return `Save $${amount} on first recharge`;
});

const durationUnit = computed(() => {
  // First try direct renewForUnit
  const directUnit =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.renewForUnit;
  if (directUnit) return directUnit;

  // If initial term type is CALENDAR, check calendar units
  const initialTermType =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.initialTermType;
  if (initialTermType === "CALENDAR") {
    const calendarInfo =
      props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.calendarInitialyActiveFor;
    if (calendarInfo?.days) return "DAY";
    if (calendarInfo?.months) return "MONTH";
    if (calendarInfo?.years) return "YEAR";
  }

  // Fallback - extract from product description
  const description = props.product?.description || "";

  // Use regex to extract duration values from description
  const dayMatch = description.match(/\b\d+\s*DAY(?:S)?\b/i);
  if (dayMatch && dayMatch[1]) {
    return "DAY";
  }

  const monthMatch = description.match(/\b\d+\s*MONTH(?:S)?\b/i);
  if (monthMatch && monthMatch[1]) {
    return "MONTH";
  }

  const yearMatch = description.match(/\b\d+\s*YEAR(?:S)?\b/i);
  if (yearMatch && yearMatch[1]) {
    return "YEAR";
  }
  // Specific common cases
  if (description.match(/365\b/i)) {
    return "DAY"; // 365 typically means 365 days
  }

  // Default to DAY if we can't determine otherwise
  return "DAY";
});

const renewalDuration = computed(() => {
  // First try direct renewFor
  const directDuration =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.renewFor;
  if (directDuration) return directDuration;

  // Check calendar duration values
  const calendarInfo =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.calendarInitialyActiveFor;
  if (calendarInfo) {
    if (calendarInfo.days) return calendarInfo.days;
    if (calendarInfo.months) return calendarInfo.months;
    if (calendarInfo.years) return calendarInfo.years;
  }

  // Extract from product name/description
  const description = props.product?.description || "";
  const dayMatch = description.match(/\b(\d+)\s*DAY\b/i);
  if (dayMatch) return dayMatch[1];

  const monthMatch = description.match(/\b(\d+)\s*MONTH\b/i);
  if (monthMatch) return monthMatch[1];

  const yearMatch = description.match(/\b(\d+)\s*YEAR\b/i);
  if (yearMatch) return yearMatch[1];

  // Check for 365 in the name, common for yearly plans expressed in days
  if (description.match(/\b365\b/)) return 365;

  return null;
});

const goCommerce = computed(() => {
  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields
    ?.pim?.goCommerce;
});

const planDurationText = computed(() => {
  // Enhanced duration display logic
  const duration = renewalDuration.value;
  const unit = durationUnit.value;

  // Add extra logging to help debug the values being used
  console.log("Plan duration values:", {
    durationUnit: unit,
    renewalDuration: duration,
    productDescription: props.product?.description,
    calendarInfo:
      props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.calendarInitialyActiveFor,
  });

  // First check standard duration/unit combinations
  if (unit === "MONTH" && duration) {
    return duration > 1 ? `${duration} months` : `month`;
  } else if (unit === "DAY" && duration) {
    return `${duration} days`;
  } else if (unit === "YEAR" && duration) {
    return duration > 1 ? `${duration} years` : `year`;
  }

  // For products with special durations in the description
  const description = props.product?.description || "";

  // Use regex to extract duration values from description
  const dayMatch = description.match(/\b(\d+)\s*DAY(?:S)?\b/i);
  if (dayMatch && dayMatch[1]) {
    return `${dayMatch[1]} days`;
  }

  const monthMatch = description.match(/\b(\d+)\s*MONTH(?:S)?\b/i);
  if (monthMatch && monthMatch[1]) {
    const months = parseInt(monthMatch[1], 10);
    return months > 1 ? `${months} months` : "month";
  }

  const yearMatch = description.match(/\b(\d+)\s*YEAR(?:S)?\b/i);
  if (yearMatch && yearMatch[1]) {
    const years = parseInt(yearMatch[1], 10);
    return years > 1 ? `${years} years` : "year";
  }

  // Specific common cases
  if (description.match(/365\b/i)) {
    return "365 days";
  }
  if (description.match(/28\b/i)) {
    return "28 days";
  }
  if (description.match(/30\b/i)) {
    return "30 days";
  }
  if (description.match(/90\b/i)) {
    return "90 days";
  }

  // Default fallback
  return "28 days";
});

// Gift card implementation moved to combined implementation above

const getDestinationCount = (destinations: string | undefined): number => {
  if (!destinations) return 0;
  const parts = destinations
    .split(/,|\sand\s/)
    .filter((part: string) => part.trim());
  return parts.length;
};

// Modal descriptions from product custom fields
const opModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.opModalDescription || "";
});
const flybuysModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.flybuysDescription || "";
});
const speedModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.networkSpeedDescription || "";
});

// Match modal icons by asset name instead of array position: Strapi's
// GraphQL API returns multiple-media fields ordered by file id, NOT by the
// order set in the admin, so index-based mapping shows the wrong icons.
// Falls back to the legacy index positions when no name matches.
const findModalImage = (keywords: string[], fallbackIndex: number) =>
  computed(() => {
    const byName = props.modalImages?.find((img) => {
      const haystack =
        `${img.name || ""} ${img.alternativeText || ""}`.toLowerCase();
      return keywords.some((keyword) => haystack.includes(keyword));
    });
    return byName || props.modalImages?.[fallbackIndex];
  });

const opModalImage = findModalImage(["onepass", "one-pass", "one_pass"], 0);
const flybuysModalImage = findModalImage(["flybuys"], 1);
const speedModalImage = findModalImage(["speed", "network", "signal"], 2);

const showOpModal = ref(false);
const showFlybuysModal = ref(false);
const showSpeedModal = ref(false);

// Event delegation: catch clicks on [data-modal] links inside rendered HTML
const handleFeaturesClick = (event: Event) => {
  const target = event.target as HTMLElement;
  const modalTrigger = target.closest("[data-modal]") as HTMLElement | null;
  if (modalTrigger) {
    event.preventDefault();
    const modalType = modalTrigger.getAttribute("data-modal");
    if (modalType === "op-modal") {
      showOpModal.value = true;
    } else if (modalType === "flybuys-modal") {
      showFlybuysModal.value = true;
    } else if (modalType === "speed-modal") {
      showSpeedModal.value = true;
    }
  }
};

const { currency } = formatterUtils();
const dayjs = useDayjs();
</script>

<template>
  <div
    :class="['card-outer', isBestValue ? 'has-best-value' : 'standard-card', hasPromotion ? 'has-promo' : 'no-promo']"
  >
    <!-- OnePass modal triggered from data-modal links in feature HTML -->
    <Teleport to="body">
      <div v-if="showOpModal" class="op-modal-overlay" @click.self="showOpModal = false">
        <div class="op-modal">
          <!-- Header -->
          <div class="op-modal-header">
            <div class="op-modal-header-left">
              <img v-if="opModalImage?.url" :src="useStrapiMedia(opModalImage.url)" :alt="opModalImage.alternativeText || 'OnePass'" class="op-modal-icon" />
              <span class="op-modal-title">OnePass membership</span>
            </div>
            <button class="op-modal-close" @click="showOpModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9900F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <!-- Body -->
          <div class="op-modal-body">
            {{ opModalDescription || 'Customers on eligible OnePass Mobile plans will receive a OnePass membership at the account level while their mobile service remains active.\n\nOnly one OnePass membership is permitted per account. Membership is valid only while the eligible mobile plan remains active.\n\nEligibility criteria and T&Cs apply.' }}
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Flybuys modal -->
    <Teleport to="body">
      <div v-if="showFlybuysModal" class="op-modal-overlay" @click.self="showFlybuysModal = false">
        <div class="op-modal">
          <div class="op-modal-header">
            <div class="op-modal-header-left">
              <img v-if="flybuysModalImage?.url" :src="useStrapiMedia(flybuysModalImage.url)" :alt="flybuysModalImage.alternativeText || 'Flybuys'" class="op-modal-icon" />
              <span class="op-modal-title" style="color: #054DFF;">Flybuys</span>
            </div>
            <button class="op-modal-close" @click="showFlybuysModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9900F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div class="op-modal-body">
            {{ flybuysModalDescription || 'Customers on eligible OnePass Mobile plans can earn 5x Flybuys points for every $1 spent on recharge, when their Flybuys account is linked to their OnePass membership.\n\nPoints are awarded on eligible recharge transactions made while your plan and membership remain active.\n\nEligibility criteria and T&Cs apply.' }}
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Speed caps modal -->
    <Teleport to="body">
      <div v-if="showSpeedModal" class="op-modal-overlay" @click.self="showSpeedModal = false">
        <div class="op-modal">
          <div class="op-modal-header">
            <div class="op-modal-header-left">
              <img v-if="speedModalImage?.url" :src="useStrapiMedia(speedModalImage.url)" :alt="speedModalImage.alternativeText || 'Speed'" class="op-modal-icon" />
              <span class="op-modal-title">Speed caps</span>
            </div>
            <button class="op-modal-close" @click="showSpeedModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9900F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div class="op-modal-body">
            {{ speedModalDescription || 'Download speed caps are the maximum potential speed for data to download to your device. Actual download speeds are typically slower and affected by factors like location, network traffic, and which device you use.' }}
          </div>
        </div>
      </div>
    </Teleport>

    <!-- IGNITE-84: bonus Flybuys points roundel, sits over the top-right corner -->
    <PlanFlybuysRoundel
      v-if="bonusFlybuysPoints > 0"
      :points="bonusFlybuysPoints"
      class="flybuys-roundel-position"
    />

    <!-- Promo banner - purple top section (any promotion, price or bonus data) -->
    <div v-if="hasPromotion" class="promo-banner">
      <span class="promo-text">
        {{ promoBannerText }}
      </span>
    </div>
    <!-- Empty placeholder banner for non-promo cards to keep alignment -->
    <div v-else-if="!isChangePlanContext" class="promo-banner-placeholder"></div>
    <!-- White inner card -->
    <div class="card-inner">
      <!-- Best value tag positioned to break through the top border -->
      <div v-if="isBestValue" class="best-value-tag">best value</div>

      <!-- Top section: badge + price + data -->
      <div class="card-top-section">
        <!-- Plan type badge -->
        <div class="plan-badge">
          {{
            getPlanTypeDisplay(
              props.product?.offerServiceTemplate?.[0]?.serviceTemplate
                ?.renewalRule?.calendarInitialyActiveFor?.code
            )
          }} Plan
        </div>

        <!-- Price + Data section -->
        <div class="price-data-section">
          <!--
            IGNITE-119: the colour marks the tile as promotional, so it follows
            hasPromotion. Only the value below stays on the monetary check,
            because with no price change there is no discounted price to show.
          -->
          <div class="price-display" :class="{ 'price-promo': hasPromotion }">
            <template
              v-if="hasDiscount && discountAmount > 0 && originalPrice && discountedPrice"
            >
              {{ discountedPrice }}
            </template>
            <template v-else>
              {{ formattedPrice }}
            </template>
          </div>
          <div class="data-amount">{{ quotaDataGb }}GB</div>
        </div>

        <!-- T&Cs description text -->
        <div v-if="hasPromotion" class="tcs-text">
          <template v-if="metadataBonusDataDesc">{{ metadataBonusDataDesc }}</template>
          <template v-else>
            For the first {{ planDurationText }}. Recharges
            {{ originalPrice }}/{{ quotaDataGb }}GB.
            <template v-if="discountDetails?.endDate">Offer ends {{ dayjs(discountDetails.endDate).format('DD/MM/YY') }}.</template>
            New services only.
          </template>
        </div>
      </div>

      <!-- Separator line -->
      <div class="separator-line"></div>

      <!-- Features section -->
      <div class="features-section" @click="handleFeaturesClick">
        <!-- Membership offer (OnePass, Flybuys etc from Strapi) -->
        <div v-if="membershipOffer && !isChangePlan" class="feature-item">
          <RawHtmlRenderer :htmlContent="membershipOffer" />
        </div>

        <!-- Plan features from Strapi -->
        <div
          v-for="(feature, index) in productFeatures"
          :key="feature.id"
          class="feature-item"
        >
          <NuxtImg
            v-if="feature?.icon"
            provider="strapi"
            :src="feature.icon.url"
            class="feature-icon"
            width="16"
            height="16"
          />
          <div>
            <div v-if="!feature.destinations" class="feature-name">
              {{ feature.name }}
            </div>
            <BaseTooltip
              v-if="feature.destinations"
              :tooltip="feature.destinations"
            >
              <span class="feature-name underline cursor-pointer"
                >{{ getDestinationCount(feature.destinations) }} selected
                destinations</span
              >
            </BaseTooltip>
          </div>
        </div>

        <!-- Legacy feature text -->
        <div v-if="featureText" class="plan-features">
          <div class="feature-text">
            <RawHtmlRenderer :htmlContent="featureText" />
          </div>
        </div>

        <!-- SLOT for SELECT button directly after features -->
        <slot name="after-features"></slot>
      </div>

      <!-- Button section -->
      <div class="button-section">
        <!-- More Info / Buy Now button -->
        <div v-if="props.ctaButton && !props.buyNow" class="w-full">
          <button
            class="cta-btn"
            @click="$router.push(typeof props.link === 'string' ? props.link : '#')"
          >
            Buy now
          </button>
        </div>
        <div v-if="shouldShowBuyNowButton && typeof props.buyNow === 'function'" class="w-full">
          <button class="cta-btn" @click="props.buyNow">Buy now</button>
        </div>

        <!-- Critical info link -->
        <div class="critical-info-section">
          <!-- Slot for content to appear right before critical-info -->
          <slot name="critical-info-prepend"></slot>
          <a href="/critical-information" class="critical-info-link">Critical Information Summary</a>
        </div>
      </div>

      <!-- Promotion/offer details at very bottom -->
      <!-- <div v-if="promotionDescription || discountDetails" class="footer-details">
        <div v-if="promotionDescription" class="promotion-text" v-html="promotionDescription"></div>
        <div v-if="discountDetails" class="offer-details-text">
          Offer ends: {{ dayjs(discountDetails.endDate).format("DD/MM/YYYY") }}.<br />
          {{ discountDetails.description }}
        </div>
      </div> -->
    </div>
  </div>
</template>

<style scoped lang="scss">
/* ==================== CARD OUTER (Purple wrapper) ==================== */
.card-outer {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  height: 100%;
  border: 1px solid #EEEEE9;
  border-radius: 12px;
  overflow: hidden;
  position: relative; /* anchors the IGNITE-84 Flybuys roundel */
}

/* ==================== IGNITE-84: FLYBUYS ROUNDEL ==================== */
/* V2 design places the roundel fully inside the card, overlapping the purple
   banner. Figma frame: card at left 100 / top 139 / width 305, roundel at
   left 275 / top 157 -> top 18px, right 10px. Nothing overflows, so the
   ancestor clipping that blocked the earlier overhang is no longer a factor. */
.flybuys-roundel-position {
  position: absolute;
  /* Figma: card at left 100 / top 139 / width 305, roundel at left 275 / top 157.
     The roundel sizes itself off the space the price leaves (see FlybuysRoundel),
     so this gap stays as designed at every card width. */
  top: 18px;
  right: 10px;
  z-index: 2;
}

/* Promo cards have purple background visible as the banner */
.has-promo {
  background: #9900F1;
}

/* Non-promo cards are white through */
.no-promo {
  background: #FFFFFF;
  border: 0; /* Remove outer border for non-promo */
}

/* Non-promo cards: border on card-inner instead of card-outer */
.no-promo .card-inner {
  border: 1px solid #EEEEE9;
}


/* ==================== PROMO BANNER ==================== */
.promo-banner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  width: 100%;
  background: #9900F1;
  flex-shrink: 0;
}

.promo-text {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #FFFFFF;
}

/* Empty placeholder to match promo-banner height for non-promo cards */
.promo-banner-placeholder {
  width: 100%;
  padding: 12px;
  min-height: 42px; /* same as promo-banner: 12px top + 18px line-height + 12px bottom */
  background: #FFFFFF; /* white to blend with no-promo card */
  flex-shrink: 0;
}

/* ==================== CARD INNER (White card) ==================== */
.card-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  gap: 24px;
  width: 100%;
  flex: 1;
  background: #FFFFFF;
  border-radius: 12px;
  position: relative;
}

/* ==================== BEST VALUE TAG ==================== */
.best-value-tag {
  background-color: #9900F1;
  color: white;
  padding: 4px 16px;
  border-radius: 20px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  white-space: nowrap;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 12px;
    padding: 3px 16px;
  }
}

.has-best-value .card-inner {
  border: 2px solid #000;
  padding-top: 32px;
}

/* ==================== TOP SECTION: Badge + Price + Data ==================== */
.card-top-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  /*
   * Floor that keeps promo and non-promo cards aligned. The fixed content above
   * the T&Cs is 154px (22px badge + 108px price/data + 2 x 12px gap), so this
   * reserves 122px for .tcs-text - six lines at 18px.
   *
   * Was 226px, which only reserved four lines. The September bonus Flybuys copy
   * in metadataBonusDataDescription runs to five, so the section grew past the
   * floor and the separator, features, Buy now and CIS link dropped on those
   * tiles only. Anything longer than six lines will do the same again.
   */
  min-height: 276px;
}

/* Plan type badge */
.plan-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  gap: 4px;
  background: #EEEEE9;
  border-radius: 6px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: #333333;
}

/* Price + Data container */
.price-data-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
}

/* Price display */
.price-display {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 48px;
  line-height: 60px;
  letter-spacing: -0.01em;
  color: #3A016A; /* Royal - default for non-promo */
}

/* Promo cards get purple price */
.price-promo {
  color: #9900F1;
}

/* Data amount */
.data-amount {
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 48px;
  line-height: 48px;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  color: #3A016A; /* Royal */
  width: 100%;
}

/* T&Cs text */
.tcs-text {
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.01em;
  color: #979797;
  width: 100%;
}

/* ==================== SEPARATOR ==================== */
.separator-line {
  width: 100%;
  height: 1px;
  background: #EEEEE9;
  flex-shrink: 0;
}

/* ==================== FEATURES SECTION ==================== */
.features-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // gap: 12px;
  width: 100%;
  flex: 1;
}

.feature-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.feature-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.feature-name {
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #333333;
}

/* ==================== PLAN FEATURES (Backend HTML) ==================== */
.plan-features {
  :deep(.plan-features) {
    width: 100%;
  }

  :deep(.plan-features-list) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    margin: 0;
    gap: 12px;
    list-style: none;
    width: 100%;
  }

  :deep(.plan-features-list li) {
    display: flex !important;
    flex-direction: row;
    align-items: flex-start !important;
    padding: 0 !important;
    gap: 8px !important;
    margin-bottom: 0 !important;
    width: 100%;
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #333333;
  }

  :deep(.plan-features-list li img) {
    width: 16px !important;
    height: 16px !important;
    flex-shrink: 0;
    margin-right: 0 !important;
    margin-top: 1px;
  }

  :deep(.plan-features-list li span) {
    flex: 1;
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #333333;
  }

  /* Flybuys row: bold blue text per Figma */
  :deep(.plan-features-list li:nth-child(2) span) {
    font-weight: 700;
    color: #054DFF;
  }
}

/* ==================== BUTTON SECTION ==================== */
.button-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  margin-top: auto; /* Push to bottom */
}

.cta-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  width: 100%;
  height: 48px;
  background: #9900F1;
  border-radius: 100px; /* Pill shape per Figma */
  border: none;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #FFFFFF;
  transition: background 0.2s ease;

  &:hover {
    background: #7D00CC;
  }

  &:active {
    background: #6A00AD;
  }
}

/* Critical info link */
.critical-info-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.critical-info-link {
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #9900F1;
  text-decoration: underline;
}

/* ==================== ONEPASS MODAL (Figma spec) ==================== */
.op-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3A016A33;
}

.op-modal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  width: 652px;
  max-width: 90vw;
  background: #FFFFFF;
  border: 1px solid #EEEEE9;
  border-radius: 10px;
  overflow: hidden;
}

.op-modal-header {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
  gap: 10px;
  width: 100%;
  border-bottom: 1px solid #EEEEE9;
}

.op-modal-header-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.op-modal-icon {
  width: 16px;
  height: 16px;
}

.op-modal-title {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #9900F1;
}

.op-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.op-modal-body {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px;
  gap: 10px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A016A;
  white-space: pre-line;
}

/* ==================== FOOTER DETAILS ==================== */
.footer-details {
  width: 100%;
  text-align: center;

  .promotion-text {
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    color: #979797;
    line-height: 18px;
  }

  .offer-details-text {
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    color: #979797;
    line-height: 18px;
    margin-top: 4px;
  }
}
</style>
